/* eslint-disable import/prefer-default-export */
import graphqlClient from 'api/graphql';
import { isNull, reduce } from 'lodash';

import { withTitleCasedDrugFields } from 'common/utils/titleCase';

import {
  createCatalogDrugGql,
  drugProfilesByNdcGql,
  getCatalogDrugProfileByNdcGql,
  listCatalogFormOptionsGql,
  listCatalogGql,
  listCatalogNdcsByProductIdGql,
  listCatalogProductsByDrugNameGql,
  listDrugCodeMappingsGql,
  listDrugProfilesByDrugNameGql,
  listDrugProfilesGql,
  markAvailableGql,
  updateCatalogDrugGql,
  upsertDrugProfileGql,
} from './gql';

export const listDrugApi = ({ page, sortBy, filter }) => {
  const defaultItemsPerPage = Number(process.env.REACT_APP_DEFAULT_ITEMS_PER_PAGE);
  const defaultPageOffset = Number(process.env.REACT_APP_DEFAULT_PAGE_OFFSET);

  return graphqlClient
    .request(listDrugProfilesGql, {
      limit: defaultItemsPerPage,
      offset: (page - 1) * defaultItemsPerPage || defaultPageOffset,
      sortBy,
      filter: reduce(
        filter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(({ listDrugProfiles }) => ({
      ...listDrugProfiles,
      result: (listDrugProfiles.result ?? []).map(withTitleCasedDrugFields),
    }));
};

export const upsertDrugProfileApi = (input) => {
  return graphqlClient
    .request(upsertDrugProfileGql, { input })
    .then(({ upsertDrugProfile }) => upsertDrugProfile);
};

export const listDrugProfilesByDrugNameApi = (drugName, filter) => {
  return graphqlClient
    .request(listDrugProfilesByDrugNameGql, { drugName, filter })
    .then(({ listDrugProfilesByDrugName }) =>
      (listDrugProfilesByDrugName ?? []).map(withTitleCasedDrugFields)
    );
};

export const drugProfilesByNdcApi = (ndc) => {
  return graphqlClient
    .request(drugProfilesByNdcGql, { ndc })
    .then(({ drugProfilesByNdc }) => (drugProfilesByNdc ?? []).map(withTitleCasedDrugFields));
};

export const getCatalogDrugProfileByNdcApi = (ndc) => {
  return graphqlClient
    .request(getCatalogDrugProfileByNdcGql, { ndc })
    .then(({ getCatalogDrugProfileByNdc }) => getCatalogDrugProfileByNdc);
};

export const listCatalogApi = (filter) => {
  return graphqlClient.request(listCatalogGql, { filter }).then(({ listCatalog }) => listCatalog);
};

// POM-6600 — the Add Inventory Item product search: active products only, one option per catalog
// row, sorted by name (listCatalog returns newest first).
export const searchActiveCatalogProductsApi = (drugName) =>
  graphqlClient
    .request(listCatalogGql, { filter: { drugName, isActive: true } })
    .then(({ listCatalog }) =>
      (listCatalog?.results ?? [])
        .flatMap((drug) =>
          (drug.variants ?? []).map((variant) => ({
            ...withTitleCasedDrugFields({
              drugName: drug.drugName,
              displayName: variant.displayName,
              strength: variant.strength,
            }),
            catalogId: variant.catalogId,
            productId: variant.productId,
            // The form's Strength field is prefilled from this and saved on the inventory row, so
            // it keeps the catalog's own casing; `strength` above is only for display.
            catalogStrength: variant.strength,
            dosageForm: variant.dosageForm,
            formAlias: variant.formAlias,
            packSizeValue: variant.packSizeValue,
            packSizeUnit: variant.packSizeUnit,
          }))
        )
        .sort((first, second) =>
          (first.displayName ?? '').localeCompare(second.displayName ?? '', undefined, {
            sensitivity: 'base',
          })
        )
    );

export const updateCatalogDrugApi = (input) => {
  return graphqlClient
    .request(updateCatalogDrugGql, { input })
    .then(({ updateCatalogDrug }) => updateCatalogDrug);
};

export const markAvailableApi = (input) => {
  return graphqlClient
    .request(markAvailableGql, { input })
    .then(({ markAvailable }) => markAvailable);
};

export const listCatalogProductsByDrugNameApi = (drugName, strength) => {
  return graphqlClient
    .request(listCatalogProductsByDrugNameGql, { drugName, strength })
    .then(({ listCatalogProductsByDrugName }) =>
      (listCatalogProductsByDrugName ?? []).map(withTitleCasedDrugFields)
    );
};

export const createCatalogDrugApi = (input) => {
  return graphqlClient
    .request(createCatalogDrugGql, { input })
    .then(({ createCatalogDrug }) => createCatalogDrug);
};

export const listDrugCodeMappingsApi = () => {
  return graphqlClient
    .request(listDrugCodeMappingsGql)
    .then(({ listDrugCodeMappings }) => listDrugCodeMappings);
};

export const listCatalogFormOptionsApi = () => {
  return graphqlClient
    .request(listCatalogFormOptionsGql)
    .then(({ listCatalogFormOptions }) => listCatalogFormOptions);
};

export const listCatalogNdcsByProductIdApi = (productId) => {
  return graphqlClient
    .request(listCatalogNdcsByProductIdGql, { productId: [productId] })
    .then(({ listCatalogNdcsByProductId }) => listCatalogNdcsByProductId);
};
